import type { ComponentProps } from 'react';
import { uniqueValues } from '~/_shared/utils/array/array.helpers';
import { type TranslationFnc } from '~/_shared/utils/hooks';
import {
  type MetricProps, type VirtualMetricInferenceSourceInfo,
} from '~/_shared/utils/metric/metrics.types';
import {
  METRICS_EXCLUDED_ROWS_COUNT, METRICS_ROWS_COUNT, perColumnGroupedMetric,
} from '~/_shared/utils/metric/useMetricData.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { getDataActionShortLabels } from '../customizeMetrics/customizeMetrics.helpers';
import {
  type GroupedMetric, type MetricsListItemComponent,
} from './item/metricsListItem.component';

export const mergeInferenceSources = (
  inferenceSources: ReadonlyArray<VirtualMetricInferenceSourceInfo | undefined>,
): VirtualMetricInferenceSourceInfo | undefined => {
  const sources = inferenceSources
    .filter(notNullsy)
    .flatMap(source => source.inferenceSources);

  if (sources.length === 0) {
    return undefined;
  }

  return {
    type: 'FillConfiguration',
    inferenceSources: uniqueValues(sources),
  };
};

const getAdditionalDataProps = (additionalData: MetricProps['additionalData']): Readonly<Pick<ComponentProps<typeof MetricsListItemComponent>, 'excludedRowCount'|'rowCount'>> => {
  const excludedRowCount = additionalData.find(data => data.name === METRICS_EXCLUDED_ROWS_COUNT);
  const rowCount = additionalData.find(data => data.name === METRICS_ROWS_COUNT);

  return {
    ...(excludedRowCount?.value ? { excludedRowCount: excludedRowCount?.value } : {}),
    ...(rowCount?.value ? { rowCount: rowCount?.value } : {}),
  };
};

export type GroupedMetricWithGroupActionFlag = GroupedMetric & ReturnType<typeof getAdditionalDataProps> &
  {
    readonly isPerGroupAction: boolean;
    readonly inferenceSources?: ReadonlyArray<VirtualMetricInferenceSourceInfo | undefined>;
  };

export const groupMetricsDataByColumn = (metrics: MetricProps[], t: TranslationFnc): ReadonlyArray<GroupedMetric & ReturnType<typeof getAdditionalDataProps>> => {
  const actionLabels = getDataActionShortLabels(t);

  return metrics.reduce((perColumnGroupedMetrics: GroupedMetricWithGroupActionFlag[], metric) => {
    const additionalDataProps = getAdditionalDataProps(metric.additionalData);

    if (metric.dataAction && perColumnGroupedMetric.includes(metric.dataAction) && metric.additionalData.length > 0) {
      return [...perColumnGroupedMetrics, {
        demographicId: metric.demographicId,
        name: metric.name,
        subheader: metric.dataAction ? actionLabels[metric.dataAction] : undefined,
        subheader2: metric.secondaryColumnName ?? t('{{count}} Groups', { count: metric.additionalData.length }),
        groups: metric.additionalData.map(item => ({
          name: item.name,
          value: item.value,
        })),
        isPerGroupAction: true,
        ...additionalDataProps,
        inferenceSources: [metric.inferenceSource],
        inferenceSource: undefined,
      }];
    }

    const groupedMetric = perColumnGroupedMetrics.findLast(item => item.name === metric.name && !item.isPerGroupAction);
    if (groupedMetric) {
      Object.assign(groupedMetric, {
        groups: [
          ...groupedMetric.groups || [],
          {
            name: metric.dataAction ? actionLabels[metric.dataAction] : metric.name,
            value: metric.value,
          },
        ],
        ...additionalDataProps,
        inferenceSources: [
          ...(groupedMetric?.inferenceSources || []),
          metric.inferenceSource,
        ],
      });
      return perColumnGroupedMetrics;
    }
    else {
      return [
        ...perColumnGroupedMetrics,
        {
          demographicId: metric.demographicId,
          name: metric.name,
          groups: [
            { name: metric.dataAction ? actionLabels[metric.dataAction] : metric.name, value: metric.value },
          ],
          isPerGroupAction: false,
          ...additionalDataProps,
          inferenceSources: [metric.inferenceSource],
          inferenceSource: undefined,
        },
      ];
    }
  }, [])
    .map(({ inferenceSources, inferenceSource, ...groupedMetric }): GroupedMetricWithGroupActionFlag => {
      const mergedInferenceSource = inferenceSources ? mergeInferenceSources(inferenceSources) : inferenceSource;

      return {
        ...groupedMetric,
        ...(mergedInferenceSource ? {
          inferenceSource: mergedInferenceSource,
        } : {}),
      };
    });
};
